/**
 * DerivationTrace — the calculation card: "How this was calculated" for a
 * computed number, laid out for a reviewer checking the method and the inputs:
 * Output, Inputs (value and where each came from), Method, How Asthra got
 * there, Software, Notes, and "Copy for audit file". The web app's
 * editor/CalculationCard.tsx is its twin.
 *
 * A number the drafter computed (calculate) or built into a table
 * (create_artefact) is in no source, so ordinary citations cannot point at
 * it. The server returns its derivation instead: the agent's reasoning
 * steps (naming inputs as {name}), the artefact operations, the method and
 * the result, plus each input with the citation of the source it was read
 * from. Every {name} renders as the input's value with an inline [n] chip;
 * clicking a chip opens that input's citation (document, page, quoted text).
 *
 * SRP: pure presentation of one derivation. Reused by the citation popover
 * (CitationDerivation) and the Action History row for calculate /
 * create_artefact (the shared ActionDerivation) — both satisfy TraceDerivation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useState } from "react";
import type { Citation, DerivationSoftware } from "../../api/client";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "6px 10px 8px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
  },
  title: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  result: {
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink,
  },
  unverified: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: "var(--danger)",
    textTransform: "none",
    letterSpacing: 0,
  },
  steps: {
    margin: 0,
    paddingLeft: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  step: {
    fontSize: "10.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
  },
  method: {
    fontSize: "10px",
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  value: {
    fontWeight: 600,
    color: inkColors.ink,
  },
  chip: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: brandColors.primary,
    background: "none",
    border: "none",
    padding: "0 1px",
    cursor: "pointer",
    verticalAlign: "super",
    "&:hover": { textDecoration: "underline" },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  inputPanel: {
    marginTop: "2px",
    padding: "4px 8px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    fontSize: "10px",
    color: inkColors.ink3,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  inputHead: {
    fontWeight: 600,
    color: inkColors.ink2,
  },
  warn: {
    color: "var(--danger)",
  },
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px" },
  copy: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "10px",
    color: inkColors.ink3,
    "&:hover": { color: inkColors.ink, textDecoration: "underline" },
  },
  part: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingTop: "5px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  partTitle: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  output: { fontSize: "13px", fontWeight: 600, color: inkColors.ink },
  small: { fontSize: "10px", color: inkColors.ink3, wordBreak: "break-word" },
  text: { fontSize: "10.5px", color: inkColors.ink2, wordBreak: "break-word" },
  inputs: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "4px" },
  inputLine: { display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: "6px", fontSize: "10.5px" },
  inputName: { fontFamily: fonts.mono, color: inkColors.ink3 },
  code: { fontFamily: fonts.mono, fontSize: "10px", color: inkColors.ink, wordBreak: "break-all" },
});

/** How an input came in, said for a reviewer. Pure — exported for tests. */
export type InputOrigin = "source" | "design" | "unsourced" | "table";
export function inputOrigin(input: Pick<TraceInput, "kind">): InputOrigin {
  if (input.kind === "design") return "design";
  if (input.kind === "unsourced" || input.kind === "literal") return "unsourced";
  if (input.kind === "ref" || input.kind === "artefact") return "table";
  return "source";
}

function sourceLine(input: TraceInput): string {
  const c = input.citation;
  if (!c) return input.source;
  return [c.document_name, c.section_name, c.page_no > 0 ? `p.${c.page_no}` : ""].filter(Boolean).join(" · ");
}

/** "sample_size_2x2_crossover" → "Sample size 2x2 crossover". Pure — exported for tests. */
export function operationLabel(operation: string | undefined): string {
  const words = (operation ?? "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

/** The module.function and the versions that ran it, or null. Pure — exported for tests. */
export function softwareLines(d: TraceDerivation): { code: string; versions: string } | null {
  const sw = d.software;
  if (!sw) return null;
  const code = [sw.module, sw.function].filter(Boolean).join(".");
  const libs = Object.entries(sw.libraries ?? {}).map(([n, v]) => `${n === "python" ? "Python" : n} ${v}`);
  const versions = [...libs, sw.code_version ? `Code version ${sw.code_version}` : ""].filter(Boolean).join(" · ");
  return code || versions ? { code, versions } : null;
}

function outputText(d: TraceDerivation): string {
  if (d.value) return `${d.value} ${d.unit}`.trim();
  return d.trace.find((s) => s.kind === "result")?.text || d.matched_values.join(", ");
}

const ORIGIN_WORD: Record<InputOrigin, string> = {
  source: "from a source",
  design: "design choice",
  unsourced: "not from a source",
  table: "input table",
};

/** The card as plain text, for the audit file. Pure — exported for tests. */
export function calculationAuditText(d: TraceDerivation): string {
  const lines = [`Calculation: ${operationLabel(d.operation) || d.kind || ""}`, `Output: ${outputText(d)}`, "Inputs:"];
  for (const input of d.inputs) {
    const origin = inputOrigin(input);
    const where = sourceLine(input);
    const quote = input.citation?.context ? ` "${input.citation.context}"` : "";
    const check = origin === "source" ? (input.verified ? ", found in source" : ", NOT found in source") : "";
    lines.push(
      `- ${input.name} = ${formatInputValue(input, input.name)} (${ORIGIN_WORD[origin]}${where ? `: ${where}` : ""}${check})${quote}`,
    );
  }
  if (d.method) lines.push(`Method: ${d.method}`);
  if (d.implementation) lines.push(`Arithmetic: ${d.implementation}`);
  const sw = softwareLines(d);
  if (sw) lines.push(`Software: ${[sw.code, sw.versions].filter(Boolean).join(" · ")}`);
  for (const n of d.notes ?? []) lines.push(`Note: ${n}`);
  lines.push(`Inputs checked against sources: ${d.verified ? "yes" : "no"}`);
  if (d.rationale) lines.push(`Reason given: ${d.rationale}`);
  return lines.join("\n");
}

/** The citation fields the input panel reads. */
type TraceCitation = Pick<Citation, "document_name" | "section_name" | "page_no" | "context">;

/** One input as the trace reads it. */
export interface TraceInput {
  name: string;
  value?: unknown;
  kind: string;
  source: string;
  verified: boolean;
  reason: string;
  citation: TraceCitation | null;
}

/** One trace step as the trace reads it. */
export interface TraceStep {
  kind: string;
  text: string;
  input_refs?: string[];
  citation_numbers?: Record<string, number>;
}

/** What DerivationTrace reads of a derivation — the citation popover's
 *  CitationDerivation and Action History's ActionDerivation both fit. */
export interface TraceDerivation {
  kind?: string;
  operation?: string;
  rationale?: string;
  implementation?: string;
  notes?: string[];
  software?: DerivationSoftware;
  method: string;
  value: string;
  unit: string;
  agent_note?: string;
  matched_values: string[];
  verified: boolean;
  trace: TraceStep[];
  inputs: TraceInput[];
}

/** A piece of a trace step: prose, or an input reference to render as value + chip. */
export type TraceSegment = { kind: "text"; text: string } | { kind: "input"; name: string };

/** Pure — exported for tests. Splits "CV {cv_percent} from …" into text and input segments. */
export function splitTraceText(text: string): TraceSegment[] {
  const out: TraceSegment[] = [];
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  let last = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    out.push({ kind: "input", name: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Pure — exported for tests. How an input's value reads inline. */
export function formatInputValue(input: TraceInput | undefined, name: string): string {
  if (!input || input.value === undefined || input.value === null) return name;
  const v = input.value;
  if (Array.isArray(v)) return v.length > 4 ? `${v.slice(0, 4).join(", ")}, …` : v.join(", ");
  return String(v);
}

export interface DerivationTraceProps {
  derivation: TraceDerivation;
}

export const DerivationTrace: React.FC<DerivationTraceProps> = ({ derivation }) => {
  const styles = useStyles();
  const [openInput, setOpenInput] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const byName = new Map(derivation.inputs.map((i) => [i.name, i]));
  const steps = derivation.trace.filter((s) => s.kind === "reason" || s.kind === "step");
  const method = derivation.trace.find((s) => s.kind === "method")?.text || derivation.method;
  const software = softwareLines(derivation);
  const label = operationLabel(derivation.operation);
  const numberOf = new Map<string, number>();
  for (const step of derivation.trace) {
    for (const [name, n] of Object.entries(step.citation_numbers ?? {})) numberOf.set(name, n);
  }

  const chip = (name: string, n: number) => (
    <button
      type="button"
      className={styles.chip}
      aria-label={`Source ${n} for ${name}`}
      aria-expanded={openInput === name}
      onClick={() => setOpenInput((cur) => (cur === name ? null : name))}
    >
      [{n}]
    </button>
  );

  // Steps name inputs by value; each source opens from the Inputs list, so it
  // has one control rather than one per mention.
  const renderStep = (step: TraceStep) =>
    splitTraceText(step.text).map((seg, i) => {
      if (seg.kind === "text") return <React.Fragment key={`t${i}`}>{seg.text}</React.Fragment>;
      const input = byName.get(seg.name);
      return (
        <React.Fragment key={`i${i}`}>
          <span className={styles.value}>{formatInputValue(input, seg.name)}</span>
          {input && !input.verified && input.kind !== "design" && <span className={styles.warn}> (unverified)</span>}
        </React.Fragment>
      );
    });

  const open = openInput ? byName.get(openInput) : undefined;
  const cit = open?.citation;

  const copy = () => {
    void navigator.clipboard?.writeText(calculationAuditText(derivation)).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <section className={styles.root} aria-label="How this was calculated">
      <div className={styles.head}>
        <div className={styles.title}>
          {derivation.kind === "artefact" ? "Table built" : "Calculation"}
          {label && derivation.kind !== "artefact" ? ` · ${label}` : ""}
        </div>
        <button type="button" className={styles.copy} onClick={copy}>
          {copied ? "Copied" : "Copy for audit file"}
        </button>
      </div>

      <div className={styles.part}>
        <div className={styles.partTitle}>Output</div>
        <div className={styles.output}>{outputText(derivation)}</div>
        <div className={mergeClasses(styles.small, !derivation.verified && styles.warn)}>
          {derivation.verified
            ? "Every input from a source was found in it."
            : "An input is not verified against its source."}
        </div>
      </div>

      {derivation.inputs.length > 0 && (
        <div className={styles.part}>
          <div className={styles.partTitle}>Inputs</div>
          <ul className={styles.inputs}>
            {derivation.inputs.map((input) => {
              const origin = inputOrigin(input);
              const n = numberOf.get(input.name);
              const bad = origin === "unsourced" || (origin === "source" && !input.verified);
              return (
                <li key={input.name}>
                  <div className={styles.inputLine}>
                    <span className={styles.inputName}>{input.name}</span>
                    {origin !== "table" && <span className={styles.value}>{formatInputValue(input, "—")}</span>}
                    {n !== undefined && chip(input.name, n)}
                  </div>
                  <div className={mergeClasses(styles.small, bad && styles.warn)}>
                    {origin === "design"
                      ? "Design choice, not read from a source"
                      : origin === "unsourced"
                        ? "Not from a source"
                        : `${sourceLine(input)}${origin === "source" && !input.verified ? " · not found there" : ""}`}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(method || derivation.implementation) && (
        <div className={styles.part}>
          <div className={styles.partTitle}>Method</div>
          {method && <div className={styles.text}>Method: {method}</div>}
          {derivation.implementation && <div className={styles.small}>Arithmetic: {derivation.implementation}</div>}
        </div>
      )}

      {steps.length > 0 && (
        <div className={styles.part}>
          <div className={styles.partTitle}>How it was worked out</div>
          <ol className={styles.steps}>
            {steps.map((step, i) => (
              <li key={`${step.kind}-${i}`} className={styles.step}>
                {renderStep(step)}
              </li>
            ))}
          </ol>
        </div>
      )}

      {software && (
        <div className={styles.part}>
          <div className={styles.partTitle}>Software</div>
          {software.code && <div className={styles.code}>{software.code}</div>}
          {software.versions && <div className={styles.small}>{software.versions}</div>}
        </div>
      )}

      {((derivation.notes ?? []).length > 0 || derivation.agent_note) && (
        <div className={styles.part}>
          <div className={styles.partTitle}>Notes</div>
          {(derivation.notes ?? []).map((n) => (
            <div key={n} className={styles.text}>
              {n}
            </div>
          ))}
          {derivation.agent_note && <div className={styles.method}>Note: {derivation.agent_note}</div>}
        </div>
      )}

      {open && (
        <section className={styles.inputPanel} aria-label={`Source of ${open.name}`}>
          <div className={styles.inputHead}>
            {open.name}
            {cit ? ` · ${sourceLine(open)}` : open.source ? ` · ${open.source}` : ""}
          </div>
          {cit?.context && <div>“{cit.context}”</div>}
          {!open.verified && open.reason && <div className={styles.warn}>Not verified: {open.reason}</div>}
        </section>
      )}
    </section>
  );
};

export default DerivationTrace;
